import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { scoreline } from "../results.js";
import { TmStandings, type TmStandingsView } from "../components/TmStandings.js";

/**
 * The player's view of a running tournament.
 *
 * Answers the three questions a player actually has: is my game on, where is
 * it, and where do I stand. Everything else is secondary, so "your board" is
 * the largest thing on the page and the pairing list is below it.
 *
 * Polls rather than holding a socket: between rounds there is nothing to
 * stream, and during a round the player's attention belongs on their board,
 * which has its own live connection.
 *
 * After the event the same page is the record of it: it opens on the final
 * standings, and every round's pairings — with a link to replay each game —
 * stay reachable through the round selector.
 */

interface Board {
  boardNo: number;
  white: string | null;
  black: string | null;
  result: string | null;
  resultSource: string | null;
  gamePublicId: string | null;
  gameState: string | null;
}

interface YourBoard {
  boardNo: number;
  colour: "w" | "b";
  opponent: string | null;
  gamePublicId: string | null;
  gameState: string | null;
  result: string | null;
  bye: boolean;
}

interface LiveView {
  state: string;
  roundsTotal: number;
  round: { number: number; state: string } | null;
  /** Rounds with pairings, for the round selector. */
  rounds: { number: number; state: string }[];
  /** The round whose boards are in `boards`. */
  shownRound: number | null;
  boards: Board[];
  yourBoard: YourBoard | null;
  breakEndsAt: number | null;
}

interface Standing {
  rank: number;
  nickname: string;
  country: string | null;
  score: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  buchholzCut1: number;
  buchholz: number;
  sonnebornBerger: number;
  rating: number | null;
}

/**
 * The tie-break order, exactly as the server ranks (standings.ts). Shown as
 * columns in this order so a reader can see why equal scores rank as they do.
 */
const TIEBREAKS: { key: "buchholzCut1" | "buchholz" | "sonnebornBerger" | "wins" | "rating"; short: string; name: string }[] = [
  { key: "buchholzCut1", short: "Bu-1", name: "Buchholz cut 1: opponents' points, lowest one dropped" },
  { key: "buchholz", short: "Bu", name: "Buchholz: sum of opponents' points" },
  { key: "sonnebornBerger", short: "SB", name: "Sonneborn-Berger: opponents' points, weighted by your result against each" },
  { key: "wins", short: "W", name: "Number of wins" },
  { key: "rating", short: "Rtg", name: "Rating" },
];

/** Which tie-break placed `row` below `above`, when their scores are equal. */
function decidedBy(above: Standing | undefined, row: Standing): (typeof TIEBREAKS)[number] | null {
  if (!above || above.score !== row.score) return null;
  return TIEBREAKS.find((t) => (above[t.key] ?? 0) !== (row[t.key] ?? 0)) ?? null;
}

const POLL_MS = 4000;

export function Live() {
  const { slug } = useParams();
  const [live, setLive] = useState<LiveView | null>(null);
  const [table, setTable] = useState<Standing[]>([]);
  /** For a tournament linked to TM: TM's official standings, shown as they are. */
  const [tmTable, setTmTable] = useState<TmStandingsView | null>(null);
  const [standingsDown, setStandingsDown] = useState(false);
  const [tab, setTab] = useState<"pairings" | "standings">("pairings");
  /** The round chosen in the selector; null follows the current round. */
  const [viewRound, setViewRound] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const openedFinished = useRef(false);

  const load = useCallback(async () => {
    const query = viewRound === null ? "" : `?round=${viewRound}`;
    const [l, s] = await Promise.all([
      fetch(`/api/tournaments/${slug}/live${query}`).then((r) => (r.ok ? r.json() : null)),
      // 503 carries "tm_unreachable" for a tournament ranked by TM.
      fetch(`/api/tournaments/${slug}/standings`).then((r) => (r.ok || r.status === 503 ? r.json() : null)),
    ]);
    if (l) {
      setLive(l);
      // A finished event is visited for its results: open on the standings,
      // once, without fighting a visitor who then switches tab.
      if (l.state === "finished" && !openedFinished.current) {
        openedFinished.current = true;
        setTab("standings");
      }
    }
    if (s?.tm) setTmTable(s.tm);
    else if (s?.standings) setTable(s.standings);
    setStandingsDown(s?.error === "tm_unreachable");
  }, [slug, viewRound]);

  useEffect(() => {
    void load();
    const poll = setInterval(() => void load(), POLL_MS);
    // A separate, faster tick so the break countdown moves smoothly without
    // polling the server every second.
    const tick = setInterval(() => setNow(Date.now()), 500);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
    };
  }, [load]);

  if (!live) return <p style={{ color: "#666" }}>Loading…</p>;

  const breakLeft =
    live.breakEndsAt === null ? null : Math.max(0, live.breakEndsAt - now);
  const finished = live.state === "finished";

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <p style={{ marginBottom: 8 }}>
        <Link to={`/t/${slug}`}>← Tournament page</Link>
      </p>

      <h1 style={{ marginTop: 0, fontSize: 24 }}>
        {finished ? (
          <>
            Final standings
            <span style={{ color: "#888", fontWeight: 400, fontSize: 16 }}>
              {" "}
              · {live.roundsTotal} rounds played
            </span>
          </>
        ) : live.round ? (
          `Round ${live.round.number}`
        ) : (
          "Not started"
        )}
        {!finished && live.round && (
          <span style={{ color: "#888", fontWeight: 400, fontSize: 16 }}>
            {" "}
            of {live.roundsTotal} · {live.round.state}
          </span>
        )}
      </h1>

      {breakLeft !== null && breakLeft > 0 && (
        <div
          style={{
            background: "#e3f2fd",
            border: "1px solid #90caf9",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 16,
          }}
        >
          <strong>Break</strong> — next round in{" "}
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 18 }}>
            {Math.floor(breakLeft / 60000)}:
            {String(Math.floor((breakLeft % 60000) / 1000)).padStart(2, "0")}
          </span>
          <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
            Stay on this page — your next game opens automatically.
          </div>
        </div>
      )}

      {(!finished || live.yourBoard) && <YourBoardPanel board={live.yourBoard} />}

      <div style={{ display: "flex", gap: 8, margin: "20px 0 12px" }}>
        <Tab on={tab === "pairings"} onClick={() => setTab("pairings")}>
          Pairings
        </Tab>
        <Tab on={tab === "standings"} onClick={() => setTab("standings")}>
          Standings
        </Tab>
      </div>

      {tab === "pairings" ? (
        <>
          {live.rounds.length > 1 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
              {live.rounds.map((r) => (
                <Tab
                  key={r.number}
                  on={r.number === live.shownRound}
                  onClick={() =>
                    setViewRound(r.number === live.round?.number ? null : r.number)
                  }
                >
                  Round {r.number}
                </Tab>
              ))}
            </div>
          )}
          <Pairings boards={live.boards} />
        </>
      ) : standingsDown ? (
        <p style={{ color: "#666" }}>
          The official standings come from the FMJD Tournament Manager, which cannot be reached right now. They will
          appear here again shortly.
        </p>
      ) : tmTable ? (
        <TmStandings view={tmTable} />
      ) : (
        <Standings rows={table} />
      )}
    </div>
  );
}

function YourBoardPanel({ board }: { board: YourBoard | null }) {
  if (!board) {
    return (
      <div style={{ ...panel, background: "#fafafa" }}>
        <span style={{ color: "#666" }}>
          You are not paired in this round. Sign in if you are taking part.
        </span>
      </div>
    );
  }

  if (board.bye) {
    return (
      <div style={{ ...panel, background: "#f3e5f5", borderColor: "#ce93d8" }}>
        <strong style={{ fontSize: 18 }}>You have a bye this round</strong>
        <div style={{ color: "#555", marginTop: 4 }}>
          A bye scores two points, the same as a win. Your next game is in the following round.
        </div>
      </div>
    );
  }

  const playable = board.gameState === "running" || board.gameState === "pending";

  return (
    <div
      style={{
        ...panel,
        background: playable ? "#e8f5e9" : "#fafafa",
        borderColor: playable ? "#a5d6a7" : "#e0e0e0",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 13, color: "#666" }}>Board {board.boardNo}</div>
          <strong style={{ fontSize: 18 }}>
            You play {board.colour === "w" ? "White" : "Black"} against{" "}
            {board.opponent ?? "—"}
          </strong>
          {board.gameState === "finished" && !board.result && (
            <div style={{ color: "#555", marginTop: 4 }}>
              Finished — 0–0: neither player appeared (FMJD Annex 3 §5.8)
            </div>
          )}
          {board.result && (
            <div style={{ color: "#555", marginTop: 4 }}>
              Finished — {describeResult(board.result, board.colour)}
            </div>
          )}
        </div>
        <span style={{ flex: 1 }} />
        {board.gamePublicId && (
          <Link
            to={`/g/${board.gamePublicId}`}
            style={{
              padding: "10px 22px",
              borderRadius: 8,
              background: playable ? "#00695c" : "#757575",
              color: "#fff",
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            {playable ? "Go to your game" : "Review game"}
          </Link>
        )}
      </div>
    </div>
  );
}

function describeResult(result: string, colour: "w" | "b"): string {
  if (result === "draw") return "draw";
  const won = (result === "white" && colour === "w") || (result === "black" && colour === "b");
  return won ? "you won" : "you lost";
}

function Pairings({ boards }: { boards: Board[] }) {
  if (boards.length === 0) {
    return <p style={{ color: "#666" }}>No pairings yet.</p>;
  }
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
      <thead>
        <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
          <th style={th}>#</th>
          <th style={th}>White</th>
          <th style={th}>Black</th>
          <th style={th}>Result</th>
          <th style={th} />
        </tr>
      </thead>
      <tbody>
        {boards.map((b) => (
          <tr key={b.boardNo} style={{ borderBottom: "1px solid #eee" }}>
            <td style={td}>{b.boardNo}</td>
            <td style={td}>{b.white ?? "—"}</td>
            <td style={td}>
              {b.black ?? <em style={{ color: "#888" }}>bye</em>}
            </td>
            <td style={td}>
              {b.result || b.resultSource === "forfeit" ? (
                <>
                  {scoreline(b.result)}
                  {b.resultSource && b.resultSource !== "play" && (
                    <span style={{ color: "#888", fontSize: 12 }}> ({b.resultSource})</span>
                  )}
                </>
              ) : b.gameState === "running" ? (
                <span style={{ color: "#00695c" }}>playing</span>
              ) : (
                "—"
              )}
            </td>
            <td style={td}>
              {b.gamePublicId && (
                <Link to={`/g/${b.gamePublicId}`} style={{ fontSize: 13 }}>
                  {b.gameState === "finished" ? "replay" : "watch"}
                </Link>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Standings({ rows }: { rows: Standing[] }) {
  if (rows.length === 0) return <p style={{ color: "#666" }}>No results yet.</p>;
  return (
    <>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
            <th style={th}>#</th>
            <th style={th}>Player</th>
            <th style={th}>Pts</th>
            <th style={th}>+/=/−</th>
            {TIEBREAKS.map((t) => (
              <th key={t.key} style={{ ...th, textAlign: "right" }} title={t.name}>
                {t.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const by = decidedBy(rows[i - 1], r);
            return (
              <tr key={r.rank} style={{ borderBottom: "1px solid #eee" }}>
                <td style={td}>{r.rank}</td>
                <td style={td}>
                  {r.nickname}
                  {r.country && (
                    <span style={{ color: "#888", fontSize: 12 }}> {r.country}</span>
                  )}
                </td>
                <td style={{ ...td, fontWeight: 600 }}>{r.score}</td>
                <td style={{ ...td, color: "#666" }}>
                  {r.wins}/{r.draws}/{r.losses}
                </td>
                {TIEBREAKS.map((t) => {
                  const deciding = by?.key === t.key;
                  return (
                    <td
                      key={t.key}
                      title={deciding ? `Below #${r.rank - 1} on ${t.name.split(":")[0]}` : undefined}
                      style={{
                        ...td,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: deciding ? "#00695c" : "#666",
                        fontWeight: deciding ? 700 : 400,
                        background: deciding ? "#e0f2f1" : undefined,
                      }}
                    >
                      {r[t.key] ?? "—"}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p style={{ fontSize: 12.5, color: "#666", marginTop: 10, lineHeight: 1.6 }}>
        Ranked by points (win 2, draw 1), then in this order:{" "}
        {TIEBREAKS.map((t) => t.name.split(":")[0]).join(" → ")}. When two players
        have the same points, the highlighted value is the one that separated them.
      </p>
    </>
  );
}

function Tab({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 16px",
        borderRadius: 6,
        border: "1px solid #ccc",
        background: on ? "#00695c" : "#fff",
        color: on ? "#fff" : "#333",
        cursor: "pointer",
        fontWeight: on ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

const panel: React.CSSProperties = {
  border: "1px solid #e0e0e0",
  borderRadius: 8,
  padding: "14px 16px",
};
const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "6px 8px" };
